/*
  <product-card-v3>
  Controller for the redesigned product card (duplicated from product-card-v2,
  with the one-time / subscribe purchase logic removed — one-time only).
  Responsibilities:
    - Quantity stepper (+ / -)
    - Weight <select> -> keep [name="id"] in sync, update the visible price
    - Wishlist heart toggle
    - Post-add "Added" feedback

  Add-to-cart is submitted by the theme's <product-form> element (product-form.js),
  so cart drawer / AJAX behaviour is inherited unchanged. No selling_plan field
  exists in the form, so lines are always added as regular one-time purchases.
*/
if (!customElements.get('product-card-v3')) {
  class ProductCardV3 extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('.pcv3__select select');
      this.idInput = this.querySelector('[data-pcv3-id]');
      this.qtyInput = this.querySelector('.pcv3__qty-input');
      this.priceAmount = this.querySelector('.pcv3__price-amount');
      this.priceWas = this.querySelector('.pcv3__price-was');
      this.atc = this.querySelector('.pcv3__atc');
      this.wish = this.querySelector('.pcv3__wish');

      // connectedCallback re-runs every time the element is moved or
      // re-inserted (grid search appends, filter re-renders, carousels).
      // Element listeners are bound once; later connects only re-attach the
      // document listener dropped in disconnectedCallback and refresh state —
      // otherwise the stepper handlers stack and one click adds 2, 3, 4…
      this.listenFavorites();
      if (this._bound) {
        this.syncWishFromStore();
        return;
      }
      this._bound = true;

      this.bindQuantity();
      this.bindSelect();
      this.bindDropdownPosition();
      this.bindWishlist();
      this.bindAddedFeedback();
      this.enforceOneTimePurchase();
    }

    /* ---------- One-time purchase only ----------
       The card ships without selling-plan fields, but subscription apps (e.g.
       Seal Subscriptions) can inject their own into any visible add-to-cart
       form. Their UI is hidden by CSS; this removes the fields themselves so
       the line is always added as a regular one-time purchase. */
    enforceOneTimePurchase() {
      const form = this.querySelector('form');
      if (!form) return;
      const strip = () => {
        form.querySelectorAll('[name="selling_plan"]').forEach((el) => el.remove());
      };
      strip();
      form.addEventListener('submit', strip, true);
    }

    getVariantId() {
      if (this.select) return this.select.value;
      if (this.idInput) return this.idInput.value;
      return null;
    }

    /* ---------- Quantity stepper ---------- */
    bindQuantity() {
      if (!this.qtyInput) return;
      const min = parseInt(this.qtyInput.min, 10) || 1;

      this.querySelectorAll('.pcv3__qty-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          const step = btn.dataset.pcv3Step === 'down' ? -1 : 1;
          const current = parseInt(this.qtyInput.value, 10) || min;
          const next = Math.max(min, current + step);
          this.qtyInput.value = next;
          this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      this.qtyInput.addEventListener('change', () => {
        const val = parseInt(this.qtyInput.value, 10);
        if (Number.isNaN(val) || val < min) this.qtyInput.value = min;
      });
    }

    /* ---------- Variant dropdown ----------
       The visible <custom-select> (same as the cart lines) writes into the
       hidden native select, but nothing writes back: keep its button and
       selected option in line when the native value changes some other way
       (initial/back-navigation restore, bundle pages locking the variant). */
    syncDropdown(opt) {
      const dropdown = this.querySelector('.pcv3__dropdown');
      if (!dropdown) return;
      const option = dropdown.querySelector(`.js-option[data-value="${opt.value}"]`);
      if (!option) return;

      dropdown.querySelectorAll('.js-option[aria-selected="true"]').forEach((el) => {
        if (el !== option) el.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');
      dropdown.selectedOption = option;
      const listbox = dropdown.querySelector('.custom-select__listbox');
      if (listbox) listbox.setAttribute('aria-activedescendant', option.id);
      const label = dropdown.querySelector('.custom-select__btn > span');
      if (label) label.textContent = option.firstElementChild.textContent;

      // Bundle pages remove the other options from the native select: mirror it.
      const allowed = new Set(Array.from(this.select.options).map((o) => o.value));
      dropdown.querySelectorAll('.js-option').forEach((el) => {
        el.hidden = !allowed.has(el.dataset.value);
      });
    }

    /* ---------- Variant dropdown position ----------
       The card (overflow: hidden) and the carousels around it clip anything
       that sticks out, so an absolutely positioned menu could only open
       upwards, inside the card. While open, the menu is positioned against the
       viewport instead: right below the button (above it when the screen has
       no room below), following the button on scroll/resize. */
    bindDropdownPosition() {
      const dropdown = this.querySelector('.pcv3__dropdown');
      const listbox = dropdown && dropdown.querySelector('.custom-select__listbox');
      const button = dropdown && dropdown.querySelector('.custom-select__btn');
      if (!listbox || !button) return;

      const place = () => {
        if (!dropdown.classList.contains('is-open')) return;
        const gap = 4;
        const btn = button.getBoundingClientRect();

        Object.assign(listbox.style, {
          position: 'fixed',
          top: '0px',
          left: '0px',
          bottom: 'auto',
          minWidth: `${Math.max(btn.width, 130)}px`
        });
        // A transformed ancestor (e.g. the open cart drawer) becomes the
        // containing block of fixed elements: measure where (0, 0) landed.
        const origin = listbox.getBoundingClientRect();
        const menuHeight = listbox.offsetHeight;
        const fitsBelow = btn.bottom + gap + menuHeight <= window.innerHeight;
        const top = fitsBelow ? btn.bottom + gap : btn.top - gap - menuHeight;

        const left = Math.min(btn.left, window.innerWidth - listbox.offsetWidth - 8);
        listbox.style.top = `${Math.round(top - origin.top)}px`;
        listbox.style.left = `${Math.round(Math.max(8, left) - origin.left)}px`;
      };

      const reset = () => {
        ['position', 'top', 'left', 'bottom', 'minWidth'].forEach((prop) => {
          listbox.style[prop] = '';
        });
      };

      let frame = null;
      const onMove = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(place);
      };

      new MutationObserver(() => {
        if (dropdown.classList.contains('is-open')) {
          place();
          window.addEventListener('scroll', onMove, { passive: true, capture: true });
          window.addEventListener('resize', onMove);
        } else {
          window.removeEventListener('scroll', onMove, { capture: true });
          window.removeEventListener('resize', onMove);
          reset();
        }
      }).observe(dropdown, { attributes: true, attributeFilter: ['class'] });
    }

    /* ---------- Variant <select> ---------- */
    bindSelect() {
      if (!this.select) return;
      this.select.addEventListener('change', () => this.syncFromSelect());
      // Align the price, hidden id and button state with the initially
      // selected option too: the browser may restore a previous (possibly
      // sold-out) selection on back-navigation.
      this.syncFromSelect();
    }

    syncFromSelect() {
      const opt = this.select.options[this.select.selectedIndex];
      if (!opt) return;

      this.syncDropdown(opt);

        if (this.idInput) this.idInput.value = opt.value;

        // Update the visible price.
        if (this.priceAmount && opt.dataset.price) {
          this.priceAmount.textContent = opt.dataset.price;
        }
        if (this.priceWas) {
          if (opt.dataset.compare) {
            this.priceWas.textContent = opt.dataset.compare;
            this.priceWas.hidden = false;
          } else {
            this.priceWas.hidden = true;
          }
        }

        // Reflect availability on the button. aria-disabled must be kept in
        // sync too: the card renders it alongside `disabled` for sold-out
        // variants, and both the styling and product-form.js's submit guard
        // read it — leaving it stale keeps the button dead after switching
        // to an in-stock variant.
        if (this.atc) {
          const unavailable = opt.dataset.available === 'false';
          this.atc.toggleAttribute('disabled', unavailable);
          if (unavailable) {
            this.atc.setAttribute('aria-disabled', 'true');
          } else {
            this.atc.removeAttribute('aria-disabled');
          }
          const label = this.atc.querySelector('.pcv3__atc-label');
          if (label) {
            label.textContent = unavailable
              ? (this.dataset.soldOutText || 'Sold out')
              : (this.dataset.addText || 'Add to Cart');
          }
        }
    }

    /* ---------- Wishlist heart (persisted via window.cibFavorites) ---------- */
    // Keep every instance of this product in sync (other cards, other
    // sections, other tabs). productId null = bulk change, re-check own state.
    // Re-attached on every connect (disconnectedCallback removes it).
    listenFavorites() {
      if (!this.wish || this._onFavoritesChange) return;
      this._onFavoritesChange = (e) => {
        const d = e.detail || {};
        if (d.productId === null || String(d.productId) === String(this.dataset.productId)) {
          this.syncWishFromStore();
        }
      };
      document.addEventListener('cib:favorites:change', this._onFavoritesChange);
    }

    bindWishlist() {
      if (!this.wish) return;

      // Restore the saved state whenever a card is rendered/connected.
      this.syncWishFromStore();

      this.wish.addEventListener('click', (e) => {
        e.preventDefault();
        let active;
        if (window.cibFavorites && this.dataset.productId) {
          active = window.cibFavorites.toggle(this.dataset.productId);
          this.setWishState(active); // own card updates instantly; peers via event
        } else {
          // Store unavailable — keep the original per-card visual toggle.
          active = this.wish.classList.toggle('is-active');
          this.wish.setAttribute('aria-pressed', active ? 'true' : 'false');
        }
        this.dispatchEvent(
          new CustomEvent('pcv3:wishlist', {
            bubbles: true,
            detail: { productId: this.dataset.productId, active }
          })
        );
      });
    }

    setWishState(active) {
      if (!this.wish) return;
      this.wish.classList.toggle('is-active', !!active);
      this.wish.setAttribute('aria-pressed', active ? 'true' : 'false');
    }

    syncWishFromStore() {
      if (window.cibFavorites && this.dataset.productId) {
        this.setWishState(window.cibFavorites.has(this.dataset.productId));
      }
    }

    disconnectedCallback() {
      if (this._onFavoritesChange) {
        document.removeEventListener('cib:favorites:change', this._onFavoritesChange);
        this._onFavoritesChange = null;
      }
    }

    /* ---------- Post-add feedback ---------- */
    bindAddedFeedback() {
      this.addEventListener('on:cart:add', () => this.showAdded());
      this.addEventListener('on:line-item:change', () => this.showAdded());
    }

    showAdded() {
      this.classList.add('is-added');
      const label = this.atc && this.atc.querySelector('.pcv3__atc-label');
      if (label) {
        const original = this.dataset.addText || label.textContent;
        label.textContent = this.dataset.addedText || 'Added ✓';
        window.setTimeout(() => {
          label.textContent = original;
          this.classList.remove('is-added');
        }, 1800);
      }
    }
  }

  customElements.define('product-card-v3', ProductCardV3);
}
